<template>
  <div v-if="review.duration || review.intensity || review.slept || edit">
    <h2 class="display-6 mb-4 d-flex align-items-center gap-2">
      <font-awesome-icon icon="fa-solid fa-clipboard-check" />
      {{ $t('review.title') }}
    </h2>

    <div class="row row-cols-3 g-2 d-none d-md-flex mb-3">
      <div class="col d-flex flex-column align-items-center text-center">
        <font-awesome-icon icon="fa-solid fa-stopwatch-20" class="fs-2 mb-2" />
        <template v-if="edit">
          <input
            v-model.number="review.duration"
            class="form-control text-center"
            type="number"
            inputmode="numeric"
            min="0"
            max="999"
            :class="{ 'is-invalid': errors.duration }"
            :placeholder="$t('review.duration')"
            @input="updateValue" />
        </template>
        <template v-else>
          <div class="lead">{{ review.duration }}</div>
          <div class="text-muted small">{{ $t('review.duration') }}</div>
        </template>
      </div>

      <div class="col d-flex flex-column align-items-center text-center">
        <font-awesome-icon icon="fa-solid fa-battery-three-quarters" class="fs-2 mb-2" />
        <IntensityStars :edit="edit" :intensity="review.intensity" @set-intensity="setIntensity" />
        <div class="text-muted small">{{ $t('review.intensity') }}</div>
      </div>

      <div class="col d-flex flex-column align-items-center text-center">
        <font-awesome-icon icon="fa-solid fa-bed" class="fs-2 mb-2" />
        <template v-if="edit">
          <input
            v-model.number="review.slept"
            type="number"
            inputmode="decimal"
            min="0"
            max="24"
            step="0.1"
            class="form-control text-center"
            :class="{ 'is-invalid': errors.slept }"
            :placeholder="$t('review.slept')"
            @input="updateValue" />
        </template>
        <template v-else>
          <div class="lead">{{ review.slept }}</div>
          <div class="text-muted small">{{ $t('review.slept') }}</div>
        </template>
      </div>
    </div>

    <div class="list-group d-md-none mb-3">
      <div class="list-group-item d-flex justify-content-between align-items-center py-2">
        <div class="d-flex align-items-center gap-2">
          <font-awesome-icon icon="fa-solid fa-stopwatch-20" />
          <span class="small">{{ $t('review.duration') }}</span>
        </div>
        <template v-if="edit">
          <input
            v-model.number="review.duration"
            type="number"
            inputmode="numeric"
            min="0"
            max="999"
            step="1"
            class="form-control text-end"
            style="width: 80px"
            :class="{ 'is-invalid': errors.duration }"
            @input="updateValue" />
        </template>
        <template v-else>
          <span class="font-monospace">{{ review.duration }}</span>
        </template>
      </div>

      <div class="list-group-item d-flex justify-content-between align-items-center py-2">
        <div class="d-flex align-items-center gap-2">
          <font-awesome-icon icon="fa-solid fa-battery-three-quarters" />
          <span class="small">{{ $t('review.intensity') }}</span>
        </div>
        <div>
          <IntensityStars
            :edit="edit"
            :intensity="review.intensity"
            @set-intensity="setIntensity" />
        </div>
      </div>

      <div class="list-group-item d-flex justify-content-between align-items-center py-2">
        <div class="d-flex align-items-center gap-2">
          <font-awesome-icon icon="fa-solid fa-bed" />
          <span class="small">{{ $t('review.slept') }}</span>
        </div>
        <template v-if="edit">
          <input
            v-model.number="review.slept"
            type="number"
            inputmode="decimal"
            min="0"
            max="24"
            step="0.1"
            class="form-control text-end"
            style="width: 80px"
            :class="{ 'is-invalid': errors.slept }"
            @input="updateValue" />
        </template>
        <template v-else>
          <span class="font-monospace">{{ review.slept }}</span>
        </template>
      </div>
    </div>
  </div>

  <div v-if="edit || review.notes" class="mb-3">
    <textarea
      v-model.trim="review.notes"
      class="form-control"
      :placeholder="$t('review.description')"
      :maxlength="maxLength"
      rows="5"
      @input="updateValue"
      :disabled="!edit" />
    <small v-if="edit" class="text-muted">
      {{ review.notes?.length || 0 }}/{{ maxLength }}
    </small>
  </div>
  <div class="ai-review" v-if="review.ai_conclusion">
    <h1 class="lead">{{ $t('review.ai') }}</h1>
    <p class="review-text small" :class="{ collapsed: !expanded }">
      {{ review.ai_conclusion }}
    </p>
    <button v-if="review.ai_conclusion.length > 100" class="btn btn-link p-0" type="button" @click="expanded = !expanded">
      {{ expanded ? $t('buttons.hide') : $t('buttons.read_full') }}
    </button>
  </div>
</template>

<script setup>
import { ref, watchEffect } from 'vue';
import IntensityStars from '../../common/IntensityStars.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  edit: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'update:isValid']);
const maxLength = 500;
const expanded = ref(false);
const defaultReview = {
  duration: 0,
  intensity: 0,
  slept: 0,
  notes: '',
};
const review = ref({
  ...defaultReview,
  ...props.modelValue,
});
const errors = ref({
  duration: false,
  intensity: false,
  slept: false,
  notes: false,
});

watchEffect(() => {
  review.value = {
    ...defaultReview,
    ...props.modelValue,
  };
});

function validateReview() {
  const result = {
    duration: !(typeof review.value.duration === 'number' && review.value.duration >= 0 && review.value.duration < 480),
    intensity: !(typeof review.value.intensity === 'number' && review.value.intensity >= 0 && review.value.intensity <= 10),
    slept: !(typeof review.value.slept === 'number' && review.value.slept >= 0 && review.value.slept <= 24),
    notes: review.value.notes?.length > maxLength,
  };

  errors.value = result;

  return !Object.values(result).some(Boolean);
}

function updateValue() {
  const valid = validateReview();
  emit('update:modelValue', { ...review.value });
  emit('update:isValid', valid);
}
function setIntensity(star) {
  review.value.intensity = star;
  updateValue();
}
</script>
<style scoped>
.ai-review {
  font-size: 0.75rem;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-line;
}

.review-text {
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.review-text.collapsed {
  max-height: 3rem;
  text-overflow: ellipsis;
}
</style>
